import type { Role } from './roles';

/**
 * The policy engine is allowlist-capable but ships configured as a denylist,
 * which is what was specified: analysts may SELECT anything except what is
 * explicitly forbidden.
 *
 * The mode lives in config rather than in code so tightening to ALLOWLIST
 * later is a settings change and a data migration, not a rewrite. Every code
 * path below already handles both.
 */
export const POLICY_MODES = ['DENYLIST', 'ALLOWLIST'] as const;
export type PolicyMode = (typeof POLICY_MODES)[number];

export const RULE_SCOPES = ['DATABASE', 'TABLE', 'COLUMN'] as const;
export type RuleScope = (typeof RULE_SCOPES)[number];

export const RULE_ACTIONS = ['DENY', 'MASK', 'ALLOW'] as const;
export type RuleAction = (typeof RULE_ACTIONS)[number];

/**
 * How a masked value is rewritten before it leaves the API. Masking happens
 * server-side on the result set — the raw value never reaches the browser, so
 * it cannot be recovered from devtools or a network trace.
 */
export const MASK_STRATEGIES = [
  /** Replace entirely: "████████". */
  'REDACT',
  /** Keep last 4: "•••• •••• •••• 4242". Good for cards, phone numbers. */
  'PARTIAL',
  /** "j••••@example.com" — preserves domain for grouping, hides identity. */
  'EMAIL',
  /** Deterministic SHA-256 keyed per column: joins still work, values don't leak. */
  'HASH',
  /** Numbers rounded to a bucket, e.g. salary → nearest 10k. */
  'BUCKET',
] as const;
export type MaskStrategy = (typeof MASK_STRATEGIES)[number];

export interface PolicyRule {
  id: string;
  dataSourceId: string | null;
  scope: RuleScope;
  /**
   * Glob-style pattern matched case-insensitively against the fully qualified
   * object name. `*` matches within one name segment, `**` across segments.
   *
   *   DATABASE  "mysql"                → the whole mysql system database
   *   TABLE     "hr.*"                 → every table in the hr database
   *   TABLE     "*.audit_*"            → audit_* tables in any database
   *   COLUMN    "*.*.password_hash"    → that column wherever it appears
   *   COLUMN    "billing.cards.number" → one specific column
   */
  pattern: string;
  action: RuleAction;
  maskStrategy: MaskStrategy | null;
  /** Null means the rule applies to every role. */
  appliesToRoles: Role[] | null;
  /** Shown to the analyst when a query is blocked. Write it for humans. */
  reason: string;
  enabled: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
}

export interface PolicyRuleInput {
  dataSourceId?: string | null;
  scope: RuleScope;
  pattern: string;
  action: RuleAction;
  maskStrategy?: MaskStrategy | null;
  appliesToRoles?: Role[] | null;
  reason: string;
  enabled?: boolean;
}

/** Why a query was refused. Returned to the console and written to the audit log. */
export interface PolicyViolation {
  code:
    | 'NOT_A_SELECT'
    | 'MULTIPLE_STATEMENTS'
    | 'PARSE_FAILED'
    | 'DENIED_TABLE'
    | 'DENIED_COLUMN'
    | 'DENIED_DATABASE'
    | 'NOT_ALLOWLISTED'
    | 'FORBIDDEN_FUNCTION'
    | 'FORBIDDEN_SYNTAX'
    | 'ROW_LIMIT_REQUIRED';
  message: string;
  /** The object that triggered it, when there is one. */
  subject?: string;
  /** Editor coordinates so Monaco can underline the offending token. */
  position?: { line: number; column: number; endLine?: number; endColumn?: number };
  /** The human-readable `reason` from the rule that matched. */
  ruleReason?: string;
}

/** Result of running a statement past the guard, before it touches the database. */
export interface PolicyDecision {
  allowed: boolean;
  violations: PolicyViolation[];
  /** Tables the statement reads, fully qualified. Recorded in the audit log. */
  referencedTables: string[];
  /** Columns that will be masked in the result set. */
  maskedColumns: Array<{ column: string; strategy: MaskStrategy }>;
  /** True when the guard appended a LIMIT the analyst did not write. */
  limitInjected: boolean;
  effectiveLimit: number;
}
